import { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PaymentHttp } from '@/services';
import { useUserContext } from '@/context';
import { useAppStore } from '@/store';

export interface WalletPageContextType {
  loading: boolean;
  transferInfo: PaymentHttp.TransferInfo;
  gatewayInfo: PaymentHttp.GatewayInfo;
  allowIncrease: boolean;
  hasSubsidy: boolean;
};

export const WalletPageContext = createContext<WalletPageContextType>({
  loading: false,
  transferInfo: null,
  gatewayInfo: null,
  allowIncrease: false,
  hasSubsidy: true
});

export const WalletPageContextProvider = ({ children }) => {

  const { profile } = useUserContext();
  const subsidyCredit = useAppStore(state => state.subsidyCredit);
  const allowIncrease = useMemo(() => profile?.group == null || !profile?.group?.onlineCreditChargeForbidden, [profile]);

  const hasSubsidy = useMemo(() => typeof subsidyCredit === 'number', [subsidyCredit]);

  const { data: transferInfo, isFetching: transferInfoLoading } = useQuery({
    queryKey: ['/transfer-info'],
    queryFn: async () => (await PaymentHttp.fetchTransferInfo()).payload
  });

  const { data: gatewayInfo, isFetching: gatewayInfoLoading } = useQuery({
    queryKey: ['/gateway-info'],
    enabled: allowIncrease,
    queryFn: async () => {
      const payload = (await PaymentHttp.fetchGateways()).payload;
      return {
        active: payload.active,
        amounts: payload.authzAmounts,
        banks: payload.authzBanks,
        canCustomAmountPay: payload.canCustomAmountPay
      };
    }
  });

  return (
    <WalletPageContext.Provider value={{
      gatewayInfo,
      transferInfo,
      allowIncrease,
      hasSubsidy,
      loading: transferInfoLoading && gatewayInfoLoading
    }}>
      {children}
    </WalletPageContext.Provider>
  );
};

export const useWalletPageContext = () => useContext(WalletPageContext);
